import React from "react";
import T from "prop-types";
import { Link } from "react-router-dom";
import _, { get } from "lodash";
import { translateProps } from "MODULES/shared/decorators";
import { shortDateYY, shortDateYYandTime } from "LIB/formatters/date";
import { dollars } from "LIB/formatters/currency";
import {
  LinkedTableRow,
  LocationFlag,
  NoteTooltip as IconTooltip,
  CompanyName,
  PartNumber
} from "MODULES/shared/components";
import { condition } from "MODULES/SalesOrders/constants";
import { QC_HOLD } from "MODULES/inventories/constants";
import { NoteTooltipTableCell } from "MODULES/shared/components/NoteTooltip";
import {
  ExchangeIcon,
  getCost,
  displayCustRmaWarning
} from "MODULES/SalesOrders/helpers";
import { getFirstNameLastInitial } from "LIB/formatters/text";
import { useCurrentUser } from "APP_ROOT/hooks";
import StatusTags from "./StatusTags";

const WarningsIcons = ({ lineItem }) => {
  const hasHazmat = lineItem.hazmat;
  const hasBioHazmat = lineItem.bio_hazmat;
  const hasContainerFee =
    lineItem.container_fee && parseFloat(lineItem.container_fee) > 0;

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {hasHazmat && (
        <IconTooltip
          icon="fa-warning"
          placement="top"
          note="Hazmat Fee"
          color="#f44336"
          style={{ marginRight: "3px" }}
        />
      )}
      {hasContainerFee && (
        <IconTooltip
          icon="fa-dollar"
          placement="top"
          note="Container Fee"
          color="#2196F3"
          style={{ marginRight: "3px" }}
        />
      )}
      {hasBioHazmat && (
        <IconTooltip
          icon={"fa-flask"}
          placement={"top"}
          note="Bio Hazmat"
          color={"#f44336"}
        />
      )}
    </div>
  );
};

WarningsIcons.propTypes = {
  lineItem: T.shape({
    hazmat: T.bool.isRequired,
    container_fee: T.oneOfType([T.string, T.number])
  }).isRequired
};

export function Row({ lineItem, statuses }) {
  const lastSoNote = lineItem.notes.map(n => n.body).join("<br/><br/>");
  const pricePerUnit = lineItem.exchange_sale
    ? lineItem.exchange_fee
    : lineItem.price_per_unit;
  const currentUser = useCurrentUser();
  const hasWareHouses = _.get(currentUser, "tenant.ware_houses", []).length > 0;
  const reserveForSale = get(lineItem, "inventory.brokered_sale", false);

  return (
    <LinkedTableRow
      to={`/sales-orders/${lineItem.sales_order_uuid}`}
      className={
        (lineItem.cancelled || lineItem.credit_exchange) && "text-muted"
      }
    >
      <td>
        {lineItem.after_hours &&
          !lineItem.internal_outright && (
            <IconTooltip
              icon="fa-clock-o"
              placement="top"
              note="After Hours"
              color="#4CAF50" // Green color for the clock icon
              style={{ marginRight: "2px" }} // Reduced margin for closer spacing
            />
          )}
        {lineItem.exporting && (
          <IconTooltip
            icon="fa-globe"
            placement="top"
            note="Exporting"
            color="#2196F3" // Blue color for the globe icon
          />
        )}
        {reserveForSale && (
          <IconTooltip
            icon="fa-bolt"
            placement="top"
            note="Brokered"
            color="#4787F8"
          />
        )}
      </td>
      {displayCustRmaWarning(lineItem) ? (
        <td>
          <IconTooltip
            icon={"fa-cog"}
            placement={"top"}
            note={"Cust. RMA"}
            color={"#FFFFFF"}
          />
        </td>
      ) : (
        <td />
      )}
      <td>{lineItem.sales_order_code}</td>
      <td>{shortDateYYandTime(lineItem.created_at)}</td>
      <td>
        <CompanyName flattenedCompany={lineItem} truncateAt={25} />
      </td>
      <td>{getFirstNameLastInitial(lineItem.seller.full_name)}</td>
      <td>{lineItem.invoice_number}</td>
      <td>{shortDateYY(lineItem.invoice_paid_date)}</td>
      <td>{lineItem.customer_purchase_order_number}</td>
      <td>
        <LocationFlag
          location={lineItem.store_owner_id}
          consignment_type={lineItem.inventory.consignment_type}
          consignedDate={lineItem.inventory.consigned_date}
          inventory={lineItem.inventory}
        />
      </td>
      <td>
        <LocationFlag location={lineItem.store_location_id} />
      </td>
      <td>
        {lineItem.location === "Shipped to Customer"
          ? "Shipped"
          : lineItem.location}
      </td>
      {hasWareHouses && (
        <td>
          {(lineItem.inventory && lineItem.inventory.ware_house_name) || ""}
        </td>
      )}
      <td className="text-center">
        <WarningsIcons lineItem={lineItem} />
      </td>
      <td>
        <PartNumber part={lineItem.part} />
      </td>
      <td>{lineItem.sell_as_alt_name}</td>
      <td>
        <Link
          onClick={e => e.stopPropagation()}
          to={`/inventories/${lineItem.inventory.uuid ||
            lineItem.inventory_id}`}
        >
          {lineItem.serial_number}
        </Link>
      </td>
      <td>{condition(lineItem.condition).abbrev}</td>
      <td className="text-center">{lineItem.quantity}</td>
      <td className="text-center">
        <ExchangeIcon lineItem={lineItem} />
      </td>
      <td className="text-right">{dollars(getCost(lineItem))}</td>
      <td className="text-right">{dollars(pricePerUnit)}</td>
      <td className="text-right">
        {lineItem.exchange_sale && dollars(lineItem.outright_value)}
      </td>
      <td className="text-right">
        {dollars(pricePerUnit - getCost(lineItem))}
      </td>
      <td>
        <StatusTags {...statuses} />
      </td>

      <NoteTooltipTableCell note={lastSoNote} />
    </LinkedTableRow>
  );
}

Row.propTypes = {
  lineItem: T.shape({
    code: T.string.isRequired,
    created_at: T.string.isRequired,
    company_name: T.string.isRequired,
    contact_name: T.string.isRequired,
    total: T.string.isRequired,
    seller: T.shape({
      full_name: T.string.isRequired
    }).isRequired
  }).isRequired,
  statuses: T.shape.isRequired
};

const statuses = lineItem => ({
  qcHold: lineItem.qc_status === QC_HOLD,
  accHold: lineItem.accounting_hold,
  qcPreapprovalRequired: lineItem.customer_qc_preapproval_required,
  exportHold: lineItem.export_compliance_hold,
  deniedPartyHold: lineItem.export_hold,
  newCompanyHold: lineItem.new_company_hold
});

const withProps = translateProps(
  s => ({
    lineItem: s,
    key: s.id,
    statuses: { ...statuses(s) }
  }),
  {
    erase: true
  }
);

export default withProps(Row);



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/Search/Row.jsx